// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { pluginFactory } from '@atlaskit/editor-common/utils';
import type { ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import type { Step } from '@atlaskit/editor-prosemirror/transform';

import { noContentChangedInStep } from '../../ui/utils/step';

import { aiDefinitionsPluginKey } from './plugin-key';
import reducer from './reducer';
import type { AIDefinitionsPluginState } from './types';

function mapping(
	tr: ReadonlyTransaction,
	pluginState: AIDefinitionsPluginState,
): AIDefinitionsPluginState {
	const { enabled, autoHighlightEnabled } = pluginState;
	let { keywordDecorations } = pluginState;

	if (!enabled || !autoHighlightEnabled) {
		return pluginState;
	}

	keywordDecorations = keywordDecorations.map(tr.mapping, tr.doc);
	return { ...pluginState, keywordDecorations };
}

function onDocChanged(
	tr: ReadonlyTransaction,
	pluginState: AIDefinitionsPluginState,
): AIDefinitionsPluginState {
	const { enabled, autoHighlightEnabled, keywordDecorationsSessionCounter } = pluginState;

	if (!enabled || !autoHighlightEnabled) {
		return pluginState;
	}

	// regex has been reset, reset counter only
	if (!pluginState.keywordRegex) {
		return {
			...pluginState,
			keywordDecorationsSessionCounter: keywordDecorationsSessionCounter + 1,
		};
	}

	// changes like bold or text color will also trigger onDocChanged,
	// but changes like these don't include real content change
	// so if there is no content changed, don't reset state
	const noContentChanged = tr.steps.every((step: Step) => noContentChangedInStep(step));
	if (noContentChanged) {
		return pluginState;
	}

	// doc content changed, reset regex, sessionId and counter
	return {
		...pluginState,
		keywordRegex: undefined,
		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		keywordDecorationsSessionId: uuid(),
		keywordDecorationsSessionCounter: keywordDecorationsSessionCounter + 1,
	};
}

export const { createCommand, getPluginState, createPluginState } = pluginFactory(
	aiDefinitionsPluginKey,
	reducer,
	{
		mapping,
		onDocChanged,
	},
);
